import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBuilding, FaCheckCircle, FaEnvelope, FaSearch, FaTools } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';
import { FEATURED_MARKET_SLUGS, MARKET_DATA } from '../data/seoLandingData';

const ClaimListingPage: React.FC = () => {
  const pageUrl = 'https://stagecrew.app/claim/vendor-listing';
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const handleClaimSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams({ claim: '1' });
    const query = search.trim();
    if (query) params.set('search', query);
    navigate(`/vendors?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Claim Your AV Company Listing | StageCrew"
        description="Confirm control of your listed business website domain, then manage supported StageCrew vendor profile details."
        url={pageUrl}
        keywords="claim AV company listing, claim event vendor listing, StageCrew vendor claim, AV company profile"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Claim Your AV Company Listing',
          description: 'Confirm a listed business website domain and manage supported vendor details.',
          url: pageUrl,
          provider: { '@type': 'Organization', name: 'StageCrew', url: 'https://stagecrew.app' },
        }}
      />

      <section className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700 mb-4">
              <FaCheckCircle /> Vendor claim workflow
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-950 tracking-normal mb-5">
              Claim your StageCrew vendor listing
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl">
              StageCrew vendor pages help event buyers find AV rental companies, staging vendors, production teams, lighting companies, audio providers, backline vendors, and labor providers. To manage a page, an employer account must confirm control of the website domain already shown on the listing.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/vendors?claim=1" className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-3 font-semibold text-white hover:bg-blue-800">
                <FaSearch /> Find your listing
              </Link>
              <a href="mailto:edwin@stagecrew.app?subject=StageCrew%20vendor%20claim%20help" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 hover:border-blue-400 hover:text-blue-700">
                <FaEnvelope /> Need help?
              </a>
            </div>
            <form onSubmit={handleClaimSearch} className="mt-6 flex max-w-2xl flex-col gap-3 sm:flex-row">
              <input
                id="vendor-claim-search"
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search your company name"
                aria-label="Search your company name"
                className="min-w-0 flex-1 rounded-lg border border-slate-300 px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white hover:bg-emerald-700">
                <FaSearch /> Search claims
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10 grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaSearch className="text-blue-700 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">Find your page</h2>
          <p className="text-sm text-slate-600 mt-2">Search the vendor directory by company name, city, or specialty.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaCheckCircle className="text-emerald-700 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">Use an employer account</h2>
          <p className="text-sm text-slate-600 mt-2">Registration returns you to the same vendor profile, but it never claims the listing automatically.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaTools className="text-amber-600 mb-3" />
          <h2 className="text-lg font-bold text-slate-950">Confirm the listed domain</h2>
          <p className="text-sm text-slate-600 mt-2">Add the one-time StageCrew TXT value in the listed website's DNS. After confirmation, you can maintain supported details.</p>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <h2 className="text-2xl font-bold text-slate-950 mb-5">Browse claim-ready markets</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {FEATURED_MARKET_SLUGS.slice(0, 12).map(slug => {
              const market = MARKET_DATA[slug];
              if (!market) return null;
              return (
                <Link key={slug} to={`/vendors/city/${market.slug}`} className="rounded-lg border border-slate-200 bg-slate-50 p-4 hover:border-blue-400 hover:bg-blue-50 transition">
                  <div className="flex items-center gap-2 font-semibold text-slate-950">
                    <FaBuilding className="text-blue-700" /> {market.name}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{market.stateAbbr} vendor listings</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10">
        <div className="rounded-lg bg-slate-950 p-8 text-white">
          <h2 className="text-2xl font-bold mb-3">What a domain-confirmed listing should include</h2>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-slate-300">
            <p>Clear service category, website, and current city/market coverage.</p>
            <p>A practical description of what your team actually provides for live events.</p>
            <p>A confirmed business website domain. This confirms domain control, not legal ownership or every business claim.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClaimListingPage;
